import React, { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { TIME_SLOTS, ROW_HEIGHT, START_HOUR, SLOT_MINUTES, getInitials, formatTime, timeToMinutes } from '../../utils';
import styles from './AppointmentCalendar.module.css';

const NOW_COLOR = 'var(--h-color-danger-500, #e73a47)';
const INACTIVE = ['Cancelled', 'No_Show'];

/** Minutes since midnight, refreshed every minute */
const useNowMinutes = (enabled) => {
    const get = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };
    const [now, setNow] = useState(get);
    useEffect(() => {
        if (!enabled) return undefined;
        const id = setInterval(() => setNow(get()), 60000);
        return () => clearInterval(id);
    }, [enabled]);
    return now;
};

export default function AppointmentCalendar({
    staffList, visibleAppointments, onEmptySlotClick, onAppointmentClick, canCreate, canEdit,
    isToday = false, onShareSchedule,
}) {
    const totalGridHeight = TIME_SLOTS.length * ROW_HEIGHT;
    const gridColumns = `72px repeat(${staffList.length > 0 ? staffList.length : 1}, minmax(180px, 1fr))`;
    const nowMinutes = useNowMinutes(isToday);

    const toTop = (minutes) => ((minutes - START_HOUR * 60) / SLOT_MINUTES) * ROW_HEIGHT;
    const getTopPos = (timeStr) => Math.max(0, toTop(timeToMinutes(timeStr)) + 2);
    const getHeightSize = (start, end) => Math.max(ROW_HEIGHT * 0.5, ((timeToMinutes(end) - timeToMinutes(start)) / SLOT_MINUTES) * ROW_HEIGHT - 4);
    const statusClass = (status) => styles[`status${status}`] || styles.statusConfirmed;

    const nowTop = toTop(nowMinutes);
    const showNow = isToday && nowTop >= 0 && nowTop <= totalGridHeight;

    if (staffList.length === 0) {
        return (
            <div className={styles.calendarContainer} style={{ display: 'grid', placeItems: 'center', minHeight: 240, color: 'var(--h-color-neutral-500, #797980)', fontWeight: 700, fontSize: 13 }}>
                No active staff found. Add staff to start booking.
            </div>
        );
    }

    return (
        <div className={styles.calendarContainer}>
            {/* Sticky header */}
            <div className={styles.calendarHeaderRow} style={{ gridTemplateColumns: gridColumns }}>
                <div className={styles.timeHeaderCell}>TIME</div>
                {staffList.map((staff) => {
                    const count = visibleAppointments.filter((a) => String(a.staffId?._id || a.staffId) === String(staff._id) && !INACTIVE.includes(a.status)).length;
                    return (
                        <div key={staff._id} className={styles.staffHeader}>
                            <div className={styles.staffAvatar}>{getInitials(staff.name)}</div>
                            <div className={styles.staffInfo} style={{ flex: 1, minWidth: 0 }}>
                                <strong>{staff.name}</strong>
                                <span>{staff.designation || 'Staff'}{count ? `, ${count} booking${count === 1 ? '' : 's'}` : ''}</span>
                            </div>
                            {onShareSchedule && (
                                <button
                                    type="button"
                                    onClick={() => onShareSchedule(staff)}
                                    title={`Send ${staff.name}'s day schedule on WhatsApp`}
                                    aria-label={`Send ${staff.name}'s day schedule on WhatsApp`}
                                    style={{
                                        display: 'grid', placeItems: 'center', width: 26, height: 26, flex: '0 0 26px',
                                        border: '1px solid var(--h-color-success-300, #7ce5a2)', borderRadius: 7,
                                        background: 'var(--h-color-success-50, #f1f9f3)', color: 'var(--h-color-success-700, #005432)', cursor: 'pointer',
                                    }}
                                >
                                    <MessageCircle size={13} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Scrollable grid */}
            <div className={styles.calendarBody} style={{ gridTemplateColumns: gridColumns, height: totalGridHeight, position: 'relative' }}>
                {/* Time axis */}
                <div className={styles.timeColumn}>
                    {TIME_SLOTS.map((slot) => (
                        <div key={slot.value} className={styles.timeRow} style={{ height: ROW_HEIGHT }}>{slot.label}</div>
                    ))}
                </div>

                {/* Staff columns */}
                {staffList.map((staff) => {
                    const staffApts = visibleAppointments.filter((a) => String(a.staffId?._id || a.staffId) === String(staff._id));
                    return (
                        <div key={staff._id} className={styles.staffColumn}>
                            {TIME_SLOTS.map((slot) => {
                                const past = isToday && timeToMinutes(slot.value) + SLOT_MINUTES <= nowMinutes;
                                return (
                                    <div
                                        key={slot.value}
                                        className={`${styles.emptySlot} ${canCreate ? styles.clickableSlot : ''}`}
                                        style={{ top: toTop(timeToMinutes(slot.value)), height: ROW_HEIGHT, ...(past ? { background: 'var(--h-color-neutral-50, #f8f9fa)' } : {}) }}
                                        onClick={() => canCreate && onEmptySlotClick(staff._id, slot.value)}
                                        title={canCreate ? `Book ${staff.name} at ${slot.label}` : undefined}
                                    />
                                );
                            })}

                            {staffApts.map((apt) => {
                                const inactive = INACTIVE.includes(apt.status);
                                const sent = apt.lastNotification || (apt.notifications?.length ? apt.notifications[apt.notifications.length - 1] : null);
                                return (
                                    <div
                                        key={apt._id}
                                        className={`${styles.appointmentCard} ${statusClass(apt.status)} ${canEdit ? styles.clickableCard : ''}`}
                                        style={{ top: getTopPos(apt.startTime), height: getHeightSize(apt.startTime, apt.endTime), ...(inactive ? { opacity: 0.55, textDecoration: 'line-through' } : {}) }}
                                        onClick={(e) => canEdit && onAppointmentClick(apt, e)}
                                        title={`${apt.clientName}, ${apt.serviceSnapshot?.name || apt.serviceName}, ${formatTime(apt.startTime)} to ${formatTime(apt.endTime)}${sent ? `. WhatsApp sent: ${sent.type}` : ''}`}
                                        role={canEdit ? 'button' : undefined}
                                        tabIndex={canEdit ? 0 : undefined}
                                        onKeyDown={(e) => { if (canEdit && (e.key === 'Enter' || e.key === ' ')) onAppointmentClick(apt, e); }}
                                    >
                                        <div className={styles.aptTime}>
                                            {formatTime(apt.startTime)} - {formatTime(apt.endTime)}
                                            {sent && <MessageCircle size={10} style={{ marginLeft: 4, verticalAlign: '-1px' }} aria-label="WhatsApp sent" />}
                                        </div>
                                        <div className={styles.aptName}>{apt.clientName}</div>
                                        <div className={styles.aptService}>{apt.serviceSnapshot?.name || apt.serviceName}</div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {/* Current time line */}
                {showNow && (
                    <div aria-hidden="true" style={{ position: 'absolute', left: 72, right: 0, top: nowTop, height: 0, borderTop: `2px solid ${NOW_COLOR}`, zIndex: 3, pointerEvents: 'none' }}>
                        <span style={{ position: 'absolute', left: -6, top: -6, width: 10, height: 10, borderRadius: '50%', background: NOW_COLOR }} />
                    </div>
                )}
            </div>
        </div>
    );
}